import LockIcon from "@mui/icons-material/Lock";
import { Box, Switch, Tooltip } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";

import React from "react";
import type { EmailTopic } from "../../models/EmailTopic";
import { RecipientTypeChips, getRecipientText } from "../RecipientTypeChips";
import { ButtonsCellRenderer } from "./ButtonsCellRenderer";

const getAddressesByType = (topic?: EmailTopic) => ({
	TO: topic?.AddressesTO,
	CC: topic?.AddressesCC,
	BCC: topic?.AddressesBCC,
});

type Props = {
	editEmailTopic: (topic: EmailTopic) => void;
	enableEmailTopic: (topic: EmailTopic) => void;
	showContextMenu: (
		event: React.MouseEvent,
		data: EmailTopic,
		configId: string,
	) => void;
};

export const useTableEmailTopics = ({
	editEmailTopic,
	enableEmailTopic,
	showContextMenu,
}: Props) => {
	const columnDefs: ColDef[] = [
		{
			field: "EmailTopicName",
			headerName: "Topic",
			flex: 2,
			minWidth: 20,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			cellRenderer: ({ data, value }: ICellRendererParams<EmailTopic>) => (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: "0.25rem",
					}}
				>
					{data?.IsSystem && (
						<Tooltip
							title={
								<>
									You can see this topic because your username is <b>admin</b>{" "}
									or <b>dexteel</b>.
								</>
							}
							slotProps={{
								tooltip: { sx: { fontSize: "0.75rem", fontWeight: 400 } },
							}}
						>
							<LockIcon sx={{ fontSize: "0.9rem" }} color="action" />
						</Tooltip>
					)}
					{value}
				</Box>
			),
		},
		{
			field: "Description",
			headerName: "Description",
			flex: 3,
			minWidth: 20,
			wrapText: true,
			autoHeight: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			valueGetter: ({ data }) => data?.Description?.trim() || "-",
		},
		{
			field: "Addresses",
			headerName: "Addresses",
			flex: 4.1,
			minWidth: 20,
			wrapText: true,
			autoHeight: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
			},
			valueGetter: ({ data }) => getRecipientText(getAddressesByType(data), 8),
			cellRenderer: ({ data }: ICellRendererParams<EmailTopic>) => (
				<RecipientTypeChips values={getAddressesByType(data)} maxVisible={8} />
			),
		},
		{
			field: "DelayTimeMinutes",
			headerName: "Delay (min)",
			flex: 0.5,
			minWidth: 20,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
			valueGetter: ({ data }) =>
				data?.DelayTimeMinutes == null ? "-" : data.DelayTimeMinutes,
		},
		{
			field: "Enabled",
			headerName: "Enabled",
			flex: 0.6,
			minWidth: 50,
			valueFormatter: ({ value }) => (!value ? "N" : "Y"),
			cellRenderer: ({ data }: ICellRendererParams<EmailTopic>) =>
				data ? (
					<Tooltip title={data.Enabled ? "Disable" : "Enable"}>
						<Switch
							checked={!!data.Enabled}
							color="primary"
							size="small"
							onClick={(event) => event.stopPropagation()}
							onDoubleClick={(event) => event.stopPropagation()}
							onChange={() => enableEmailTopic(data)}
						/>
					</Tooltip>
				) : null,
		},
		{
			headerName: "",
			field: "-",
			cellRenderer: ButtonsCellRenderer,
			cellRendererParams: {
				editEmailTopic,
				showContextMenu,
			},
			sortable: false,
			filter: false,
			flex: 0.5,
			minWidth: 80,
			wrapText: false,
			autoHeight: false,
			wrapHeaderText: false,
			autoHeaderHeight: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		},
	];

	return { columnDefs };
};
